import React from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Collapse from "react-bootstrap/Collapse";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { gettext } from "i18n";
import { useModalContext } from "state/modal";
import { SourceVisualSettings } from "utils/widgets";
import { getChartSeriesColor } from "utils/datacharts";

interface DatasourceSettingsModalProps {
  sourceUUID: string;
  sourceName: string;
  sourceIndex: number;
  currentSettings: SourceVisualSettings | undefined;
  onSave: (settings: SourceVisualSettings) => void;
}

const DatasourceSettingsModal = ({
  sourceUUID,
  sourceName,
  sourceIndex,
  currentSettings,
  onSave,
}: DatasourceSettingsModalProps) => {
  const { setActiveModal } = useModalContext();
  const onHide = () => setActiveModal(null);

  const defaultColor = getChartSeriesColor(sourceIndex);

  const [editedSettings, setEditedSettings] =
    React.useState<SourceVisualSettings>({
      sourceUUID,
      fontSize: currentSettings?.fontSize ?? 12,
      fontColor: currentSettings?.fontColor ?? defaultColor,
      lineColor: currentSettings?.lineColor ?? defaultColor,
      axisScaleManual: currentSettings?.axisScaleManual ?? false,
      axisScaleMin: currentSettings?.axisScaleMin ?? 0,
      axisScaleMax: currentSettings?.axisScaleMax ?? 100,
      showStationName: currentSettings?.showStationName ?? true,
      showStationId: currentSettings?.showStationId ?? true,
      showSourceName: currentSettings?.showSourceName ?? true,
      showUnit: currentSettings?.showUnit ?? true,
      limitWarningLow: currentSettings?.limitWarningLow ?? null,
      limitCriticalLow: currentSettings?.limitCriticalLow ?? null,
      limitWarningHigh: currentSettings?.limitWarningHigh ?? null,
      limitCriticalHigh: currentSettings?.limitCriticalHigh ?? null,
    });

  const [lowerLimitsOpen, setLowerLimitsOpen] = React.useState(
    currentSettings?.limitWarningLow != null ||
      currentSettings?.limitCriticalLow != null,
  );
  const [upperLimitsOpen, setUpperLimitsOpen] = React.useState(
    currentSettings?.limitWarningHigh != null ||
      currentSettings?.limitCriticalHigh != null,
  );

  const handleSave = () => {
    onSave(editedSettings);
    onHide();
  };

  const updateSetting = <K extends keyof SourceVisualSettings>(
    key: K,
    value: SourceVisualSettings[K],
  ) => {
    setEditedSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{gettext("Data Source Settings")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">{sourceName}</p>
        <Form>
          <h6>{gettext("Appearance")}</h6>
          <Form.Group className="mb-3">
            <Form.Label>{gettext("Font size")}</Form.Label>
            <Form.Control
              type="number"
              min={8}
              max={24}
              value={editedSettings.fontSize}
              onChange={(e) =>
                updateSetting("fontSize", Number(e.target.value))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{gettext("Font color")}</Form.Label>
            <Form.Control
              type="color"
              value={editedSettings.fontColor}
              onChange={(e) => updateSetting("fontColor", e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{gettext("Line color")}</Form.Label>
            <Form.Control
              type="color"
              value={editedSettings.lineColor}
              onChange={(e) => updateSetting("lineColor", e.target.value)}
            />
          </Form.Group>

          <h6 className="mt-4">{gettext("Name components")}</h6>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label={gettext("Station name")}
              checked={editedSettings.showStationName}
              onChange={(e) =>
                updateSetting("showStationName", e.target.checked)
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label={gettext("Station ID")}
              checked={editedSettings.showStationId}
              onChange={(e) => updateSetting("showStationId", e.target.checked)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label={gettext("Data source name")}
              checked={editedSettings.showSourceName}
              onChange={(e) =>
                updateSetting("showSourceName", e.target.checked)
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label={gettext("Unit")}
              checked={editedSettings.showUnit}
              onChange={(e) => updateSetting("showUnit", e.target.checked)}
            />
          </Form.Group>

          <h6 className="mt-4">{gettext("Axis Scaling")}</h6>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label={gettext("Manual axis scaling")}
              checked={editedSettings.axisScaleManual}
              onChange={(e) =>
                updateSetting("axisScaleManual", e.target.checked)
              }
            />
          </Form.Group>

          {editedSettings.axisScaleManual && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>{gettext("Minimum value")}</Form.Label>
                <Form.Control
                  type="number"
                  value={editedSettings.axisScaleMin}
                  onChange={(e) =>
                    updateSetting("axisScaleMin", Number(e.target.value))
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{gettext("Maximum value")}</Form.Label>
                <Form.Control
                  type="number"
                  value={editedSettings.axisScaleMax}
                  onChange={(e) =>
                    updateSetting("axisScaleMax", Number(e.target.value))
                  }
                />
              </Form.Group>
            </>
          )}

          <h6 className="mt-4">{gettext("Limit values")}</h6>
          <p className="text-muted">
            {gettext(
              "When the value leaves the safe band, the widget background is colored. All limits are optional.",
            )}
          </p>

          <div className="mb-2">
            <button
              type="button"
              className="btn btn-link p-0 d-flex align-items-center justify-content-between"
              onClick={() => setLowerLimitsOpen(!lowerLimitsOpen)}
            >
              <span style={{ fontWeight: 600 }}>
                {gettext("Lower limits")}{" "}
                <small className="text-muted">
                  {gettext("(values below are bad)")}
                </small>
              </span>
              <FontAwesomeIcon
                icon={lowerLimitsOpen ? faChevronUp : faChevronDown}
              />
            </button>
            <Collapse in={lowerLimitsOpen}>
              <div>
                <Form.Group className="mb-2">
                  <Form.Label>{gettext("Warning (yellow)")}</Form.Label>
                  <Form.Control
                    type="number"
                    value={editedSettings.limitWarningLow ?? ""}
                    onChange={(e) =>
                      updateSetting(
                        "limitWarningLow",
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{gettext("Critical (red)")}</Form.Label>
                  <Form.Control
                    type="number"
                    value={editedSettings.limitCriticalLow ?? ""}
                    onChange={(e) =>
                      updateSetting(
                        "limitCriticalLow",
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                  />
                </Form.Group>
              </div>
            </Collapse>
          </div>

          <div className="mb-2">
            <button
              type="button"
              className="btn btn-link p-0 d-flex align-items-center justify-content-between"
              onClick={() => setUpperLimitsOpen(!upperLimitsOpen)}
            >
              <span style={{ fontWeight: 600 }}>
                {gettext("Upper limits")}{" "}
                <small className="text-muted">
                  {gettext("(values above are bad)")}
                </small>
              </span>
              <FontAwesomeIcon
                icon={upperLimitsOpen ? faChevronUp : faChevronDown}
              />
            </button>
            <Collapse in={upperLimitsOpen}>
              <div>
                <Form.Group className="mb-2">
                  <Form.Label>{gettext("Warning (yellow)")}</Form.Label>
                  <Form.Control
                    type="number"
                    value={editedSettings.limitWarningHigh ?? ""}
                    onChange={(e) =>
                      updateSetting(
                        "limitWarningHigh",
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{gettext("Critical (red)")}</Form.Label>
                  <Form.Control
                    type="number"
                    value={editedSettings.limitCriticalHigh ?? ""}
                    onChange={(e) =>
                      updateSetting(
                        "limitCriticalHigh",
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                  />
                </Form.Group>
              </div>
            </Collapse>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {gettext("Cancel")}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {gettext("Save Settings")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DatasourceSettingsModal;
